/**
 * Authentication Context
 * Provides global authentication state and methods
 */

import { api } from '@/lib/api';
import type { AuthContextType, User } from '@/types/auth';
import { defaultBackofficePath } from '@fame/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const AuthContext = createContext<AuthContextType | null>(null);

const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // A ref to the next scheduled timeout ID. Using setTimeout (not setInterval)
  // so each refresh only schedules the next one after it completes — making
  // concurrent refresh calls structurally impossible and eliminating the
  // stale-token race that token rotation would otherwise trigger.
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useCallback gives stopTokenRefresh and startTokenRefresh stable references
  // so they can be listed as effect dependencies without causing infinite loops.
  const stopTokenRefresh = useCallback(() => {
    if (refreshTimeoutRef.current !== null) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const startTokenRefresh = useCallback(() => {
    stopTokenRefresh();

    const scheduleNext = () => {
      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          await api.post('/auth/refresh', {});
          // Only schedule the next refresh after this one succeeds.
          scheduleNext();
        } catch {
          // Refresh failed (e.g. rotated token already used, or network error).
          // Dispatch the unauthorised event so the handler below clears state
          // and redirects to login — stopTokenRefresh is called there too.
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
      }, REFRESH_INTERVAL_MS);
    };

    scheduleNext();
  }, [stopTokenRefresh]);

  // Fetch current user on mount to validate session
  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me');
      } catch {
        // Not authenticated — expected on first load or after session expiry
        return null;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // The query cache is the source of truth for a restored session; local state
  // only covers the interval after a successful login before it is refetched.
  useEffect(() => {
    if (data) {
      startTokenRefresh();
    } else if (error) {
      stopTokenRefresh();
    }
  }, [data, error, startTokenRefresh, stopTokenRefresh]);

  // Listen for unauthorised events dispatched by the API client or the refresh cycle
  useEffect(() => {
    const handleUnauthorized = () => {
      const wasAuthenticated = !!user;

      setUser(null);
      stopTokenRefresh();
      queryClient.clear();
      navigate('/login');

      if (wasAuthenticated) {
        toast.error('Sessie verlopen, log opnieuw in');
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate, queryClient, stopTokenRefresh, user]);

  // Cancel any pending refresh on unmount
  useEffect(() => {
    return stopTokenRefresh;
  }, [stopTokenRefresh]);

  const login = async (email: string, password: string) => {
    try {
      const userData = await api.post<User>('/auth/login', { email, password });
      setUser(userData);
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      startTokenRefresh();
      toast.success('Succesvol ingelogd');
      // Hard redirect so the browser always fetches a fresh index.html after a deploy.
      // A client-side navigate() would keep the old JS bundle alive indefinitely.
      window.location.replace(defaultBackofficePath(userData.roles));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fout bij inloggen';
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', {});
    } catch {
      // Logout might fail if already logged out, but we proceed anyway
    } finally {
      setUser(null);
      stopTokenRefresh();
      queryClient.clear();
      navigate('/login');
      toast.success('Succesvol uitgelogd');
    }
  };

  // Derive current user from either state or query data.
  // This prevents a timing gap where isLoading becomes false before the
  // useEffect above has had a chance to call setUser(data).
  const currentUser = user ?? data ?? null;

  const value: AuthContextType = {
    user: currentUser,
    isLoading,
    isAuthenticated: !!currentUser,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
